import React, { createContext, useMemo, ReactElement, useContext } from 'react';
import { Option } from 'fp-ts/lib/Option';

import { ErrorData } from 'domain/types';
import Settings from 'domain/entity/settings';
import { ReadSettings } from 'use-case/settings/read/readSettingsUseCase';
import { useCase, UseCaseState, UseCaseDispatch, CriteriaCallback } from 'adapter/in/common/hooks/useCase';

export type MaybeSettingsError = Option<ErrorData>;
export type MaybeSettings = Option<Settings>;

export type SettingsState = UseCaseState & { settings: Option<Settings> };

export const SettingsStateContext = createContext({} as SettingsState);
export const useSettings = (): SettingsState => useContext(SettingsStateContext);

type SettingsDispatch = UseCaseDispatch & { setSettings: (settings: Settings) => void; read: CriteriaCallback };

const SettingsDispatchContext = createContext({} as SettingsDispatch);
export const dispatchSettings = (): SettingsDispatch => useContext(SettingsDispatchContext);

type SettingsProviderType = {
    readSettings: ReadSettings;
    children: ReactElement;
};

export const SettingsProvider = (props: SettingsProviderType): ReactElement => {
    const { children, readSettings } = props;
    const { isLoading, data, error, setLoading, setData, setError, execute } = useCase(readSettings, false);
    const valueSettingsState = useMemo(() => ({ isLoading, settings: data, error }), [isLoading, data, error]);
    const valueSettingsDispatch = useMemo(
        () => ({
            setLoading,
            setSettings: setData,
            setError,
            read: execute,
        }),
        [setLoading, setData, setError]
    );
    return (
        <SettingsStateContext.Provider value={valueSettingsState}>
            <SettingsDispatchContext.Provider value={valueSettingsDispatch}>{children}</SettingsDispatchContext.Provider>
        </SettingsStateContext.Provider>
    );
};
